import React, { useMemo } from 'react';
import { formatCurrency } from '../../../Help/helper';
import {
  computeBalances,
  computeRevenueSummary,
  computeCapitalSummary,
  computeOpexSummary,
  computeVehicleJobDetails,
  computeCashPosition,
} from '../utils/reportAggregations';

const SectionHeader = ({ title }) => (
  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 rounded-t-xl">
    <h3 className="text-lg font-bold text-white">{title}</h3>
  </div>
);

const ReportTab = ({ data, weekStart, weekEnd }) => {
  const { preWeekTransactions, weekTransactions, weekInvoices, jobsByNo, customersByName } = data;

  const balances = useMemo(
    () => computeBalances(preWeekTransactions, weekTransactions),
    [preWeekTransactions, weekTransactions]
  );

  const revenueSummary = useMemo(
    () => computeRevenueSummary(weekTransactions),
    [weekTransactions]
  );

  const capitalSummary = useMemo(
    () => computeCapitalSummary(weekTransactions),
    [weekTransactions]
  );

  const opexSummary = useMemo(
    () => computeOpexSummary(weekTransactions),
    [weekTransactions]
  );

  const jobDetails = useMemo(
    () => computeVehicleJobDetails(weekInvoices, jobsByNo, customersByName),
    [weekInvoices, jobsByNo, customersByName]
  );

  const cashPosition = useMemo(
    () => computeCashPosition(weekTransactions),
    [weekTransactions]
  );

  const totalRevenue = revenueSummary.reduce((s, r) => s + r.amount, 0);
  const totalCapital = capitalSummary.reduce((s, r) => s + r.amount, 0);
  const totalOpex = opexSummary.reduce((s, r) => s + r.amount, 0);

  // Split job details by customer type
  const fleetJobs = jobDetails.filter(j => j.customerType === 'Fleet/Credit');
  const cashJobs = jobDetails.filter(j => j.customerType === 'Cash');

  return (
    <div className="space-y-6">
      {/* Balances */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Account Balances" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Opening</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Week Movement</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {['Revenue', 'Opex', 'CAPEX', 'Other'].map(acct => (
                <tr key={acct} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">{acct}</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-700">{formatCurrency(balances.opening[acct])}</td>
                  <td className={`px-6 py-3 text-sm text-right font-medium ${balances.delta[acct] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(balances.delta[acct])}
                  </td>
                  <td className="px-6 py-3 text-sm text-right font-semibold text-gray-900">{formatCurrency(balances.closing[acct])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Revenue Summary" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode of Payment</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {revenueSummary.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center text-gray-400 text-sm">No revenue transactions this week</td></tr>
              ) : (
                revenueSummary.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{r.description}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{r.invoiceNo || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{r.modeOfPayment}</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-green-600">{formatCurrency(r.amount)}</td>
                  </tr>
                ))
              )}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={3} className="px-6 py-3 text-sm text-gray-900">Total Revenue</td>
                <td className="px-6 py-3 text-sm text-right text-green-700">{formatCurrency(totalRevenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Capital Summary */}
      {capitalSummary.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <SectionHeader title="Capital Summary" />
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode of Payment</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {capitalSummary.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{r.description}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{r.modeOfPayment}</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-blue-600">{formatCurrency(r.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={2} className="px-6 py-3 text-sm text-gray-900">Total Capital</td>
                  <td className="px-6 py-3 text-sm text-right text-blue-700">{formatCurrency(totalCapital)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Opex Summary */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Operating Expenditure" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode of Payment</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {opexSummary.length === 0 ? (
                <tr><td colSpan={4} className="px-6 py-4 text-center text-gray-400 text-sm">No expenditure transactions this week</td></tr>
              ) : (
                opexSummary.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{r.description}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{r.category}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{r.modeOfPayment}</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-red-600">{formatCurrency(r.amount)}</td>
                  </tr>
                ))
              )}
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={3} className="px-6 py-3 text-sm text-gray-900">Total Opex</td>
                <td className="px-6 py-3 text-sm text-right text-red-700">{formatCurrency(totalOpex)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle Job Details — Fleet */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Vehicle Job Details — Fleet / Credit" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Parts</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Labour</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fleetJobs.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-4 text-center text-gray-400 text-sm">No fleet jobs this week</td></tr>
              ) : (
                fleetJobs.map((j, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{j.invoiceNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{j.customerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{j.registrationNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{j.jobType}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.spareParts)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.labour)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.vat)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(j.total)}</td>
                  </tr>
                ))
              )}
              {fleetJobs.length > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={4} className="px-4 py-3 text-sm text-gray-900">Fleet Total</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(fleetJobs.reduce((s, j) => s + j.spareParts, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(fleetJobs.reduce((s, j) => s + j.labour, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(fleetJobs.reduce((s, j) => s + j.vat, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(fleetJobs.reduce((s, j) => s + j.total, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle Job Details — Cash */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Vehicle Job Details — Cash" />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Parts</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Labour</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cashJobs.length === 0 ? (
                <tr><td colSpan={8} className="px-6 py-4 text-center text-gray-400 text-sm">No cash jobs this week</td></tr>
              ) : (
                cashJobs.map((j, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{j.invoiceNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{j.customerName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{j.registrationNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{j.jobType}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.spareParts)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.labour)}</td>
                    <td className="px-4 py-3 text-sm text-right">{formatCurrency(j.vat)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(j.total)}</td>
                  </tr>
                ))
              )}
              {cashJobs.length > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={4} className="px-4 py-3 text-sm text-gray-900">Cash Total</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(cashJobs.reduce((s, j) => s + j.spareParts, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(cashJobs.reduce((s, j) => s + j.labour, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(cashJobs.reduce((s, j) => s + j.vat, 0))}</td>
                  <td className="px-4 py-3 text-sm text-right">{formatCurrency(cashJobs.reduce((s, j) => s + j.total, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grand Total */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-3 rounded-t-xl">
          <h3 className="text-lg font-bold text-white">Week Grand Total — Job Revenue</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase mb-1">Spare Parts</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(jobDetails.reduce((s, j) => s + j.spareParts, 0))}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase mb-1">Labour</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(jobDetails.reduce((s, j) => s + j.labour, 0))}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase mb-1">VAT</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(jobDetails.reduce((s, j) => s + j.vat, 0))}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase mb-1">Grand Total</p>
              <p className="text-xl font-bold text-indigo-600">{formatCurrency(jobDetails.reduce((s, j) => s + j.total, 0))}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Position */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <SectionHeader title="Weekly Cash Position" />
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-xs text-green-600 uppercase font-medium mb-1">Total Inflows</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(cashPosition.totalInflows)}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-4 text-center">
              <p className="text-xs text-red-600 uppercase font-medium mb-1">Total Outflows</p>
              <p className="text-xl font-bold text-red-700">{formatCurrency(cashPosition.totalOutflows)}</p>
            </div>
            <div className={`rounded-xl p-4 text-center ${cashPosition.net >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
              <p className={`text-xs uppercase font-medium mb-1 ${cashPosition.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>Net Cash</p>
              <p className={`text-xl font-bold ${cashPosition.net >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>{formatCurrency(cashPosition.net)}</p>
            </div>
          </div>

          {/* Outflows by category */}
          {Object.keys(cashPosition.outflowsByCategory).length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expense Category</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Object.entries(cashPosition.outflowsByCategory)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cat, amt]) => (
                      <tr key={cat} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm text-gray-900">{cat}</td>
                        <td className="px-6 py-3 text-sm text-right font-medium text-red-600">{formatCurrency(amt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportTab;
